import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { apiFetch } from "../lib/api";

interface WalkInResponse {
  familyId: string;
  parentId: string;
  childId: string;
  screeningId: string;
  reused_family: boolean;
}

function WalkIn() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const form = new FormData(e.currentTarget);
    const payload = {
      parent: {
        fullName: form.get("parentName") as string,
        relationship: form.get("relationship") as string,
        phone: form.get("phone") as string,
        email: (form.get("email") as string) || undefined,
        preferredLanguage: form.get("language") as string,
      },
      child: {
        fullName: form.get("childName") as string,
        dob: form.get("dob") as string,
        gender: (form.get("gender") as string) || undefined,
        grade: (form.get("grade") as string) || undefined,
      },
      family: {
        primaryAddress: (form.get("address") as string) || undefined,
        city: (form.get("city") as string) || undefined,
        pincode: (form.get("pincode") as string) || undefined,
      },
      reasonForVisit: form.get("reason") as string,
    };
    try {
      const res = await apiFetch<WalkInResponse>("/api/walk-ins", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      // Open an activation conversation immediately and navigate.
      const act = await apiFetch<{ conversationId: string }>("/api/activations", {
        method: "POST",
        body: JSON.stringify({ familyId: res.familyId, childId: res.childId }),
      });
      navigate({
        to: "/activation/$conversationId",
        params: { conversationId: act.conversationId },
        search: { familyId: res.familyId, childId: res.childId },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="panel">
      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">
        Screen 2
      </div>
      <h1 className="text-2xl font-semibold mb-4">New Walk-In</h1>

      <form onSubmit={onSubmit} className="grid grid-cols-2 gap-4">
        <fieldset className="col-span-2 grid grid-cols-2 gap-4 border-t pt-4">
          <legend className="col-span-2 font-medium text-slate-700">
            Parent
          </legend>
          <Input name="parentName" label="Full name" required />
          <Select
            name="relationship"
            label="Relationship"
            options={[
              ["mother", "Mother"],
              ["father", "Father"],
              ["guardian", "Guardian"],
              ["other", "Other"],
            ]}
          />
          <Input
            name="phone"
            label="Phone (E.164 IN)"
            placeholder="+919xxxxxxxxx"
            pattern="^\+91[6-9]\d{9}$"
            required
          />
          <Input name="email" label="Email (optional)" type="email" />
          <Select
            name="language"
            label="Preferred language"
            options={[
              ["en", "English"],
              ["hi", "Hindi"],
              ["kn", "Kannada"],
              ["ta", "Tamil"],
              ["od", "Odia"],
            ]}
          />
        </fieldset>

        <fieldset className="col-span-2 grid grid-cols-2 gap-4 border-t pt-4">
          <legend className="col-span-2 font-medium text-slate-700">
            Child
          </legend>
          <Input name="childName" label="Full name" required />
          <Input name="dob" label="Date of birth" type="date" required />
          <Select
            name="gender"
            label="Gender"
            options={[
              ["", "—"],
              ["F", "Female"],
              ["M", "Male"],
              ["O", "Other"],
            ]}
          />
          <Input name="grade" label="School grade (optional)" />
        </fieldset>

        <fieldset className="col-span-2 grid grid-cols-3 gap-4 border-t pt-4">
          <legend className="col-span-3 font-medium text-slate-700">
            Family (optional)
          </legend>
          <Input name="address" label="Address" />
          <Input name="city" label="City" />
          <Input name="pincode" label="Pincode" pattern="^\d{6}$" />
        </fieldset>

        <fieldset className="col-span-2 border-t pt-4">
          <legend className="font-medium text-slate-700">Reason</legend>
          <textarea
            name="reason"
            required
            rows={3}
            className="w-full border border-slate-300 rounded px-2 py-1"
            placeholder="Why are they here today?"
          />
        </fieldset>

        {error && (
          <div className="col-span-2 text-red-600 text-sm">{error}</div>
        )}

        <div className="col-span-2 flex justify-end gap-2 border-t pt-4">
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-slate-900 text-white px-4 py-2 disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Save & open activation"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({
  name,
  label,
  type,
  required,
  placeholder,
  pattern,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  pattern?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm text-slate-600">
        {label}
        {required && <span className="text-red-500">*</span>}
      </span>
      <input
        name={name}
        type={type ?? "text"}
        required={required}
        placeholder={placeholder}
        pattern={pattern}
        className="border border-slate-300 rounded px-2 py-1"
      />
    </label>
  );
}

function Select({
  name,
  label,
  options,
}: {
  name: string;
  label: string;
  options: Array<[string, string]>;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm text-slate-600">{label}</span>
      <select
        name={name}
        className="border border-slate-300 rounded px-2 py-1 bg-white"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

export const Route = createFileRoute("/walk-in")({ component: WalkIn });
